// @spec INC-STR-007
'use client';

import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Skeleton } from '@/components/ui/skeleton';
import {
  CategoryList,
  CreateCategoryDialog,
  DeleteCategoryDialog,
  EditCategoryDialog,
  useUpdateIncidentCategory,
} from '@/features/incidents/admin/categories';
import type { IncidentCategoryAdmin } from '@/features/incidents/admin/categories';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/settings/incident-categories')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.incidentCategories'),
  }),
  component: IncidentCategoriesPage,
});

/**
 * Incident categories management page.
 *
 * Shows list of incident categories and allows admins
 * to create, edit, toggle active, and delete categories.
 *
 * System categories are displayed but cannot be modified.
 */
function IncidentCategoriesPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { isAdmin, isLoading: isLoadingRole } = useOrgRole();

  const [createOpen, setCreateOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<IncidentCategoryAdmin | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<IncidentCategoryAdmin | null>(null);

  const { mutateAsync: updateCategory } = useUpdateIncidentCategory({
    onSuccess: (data) => {
      toast.success(
        data.isActive ? `Category "${data.name}" activated` : `Category "${data.name}" deactivated`
      );
    },
    onError: (error) => {
      toast.error(error.message || i18n.t('settings:toast.failedToUpdateCategory'));
    },
  });

  useEffect(() => {
    if (!isLoadingRole && !isAdmin) {
      void navigate({ to: '/dashboard' });
    }
  }, [isLoadingRole, isAdmin, navigate]);

  if (!isLoadingRole && !isAdmin) {
    return null;
  }

  const orgId = (organization?.publicMetadata as { internalId?: string } | undefined)?.internalId;

  if (isLoadingRole || !orgId) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-4xl space-y-6">
          <header>
            <Skeleton className="h-9 w-56" />
            <Skeleton className="h-5 w-96 mt-2" />
          </header>
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  const handleToggleActive = async (category: IncidentCategoryAdmin) => {
    await updateCategory({
      categoryId: category.id,
      data: {
        isActive: !category.isActive,
      },
    });
  };

  return (
    <div className="p-8">
      <div className="mx-auto max-w-4xl space-y-6">
        <header>
          <h1 className="text-3xl font-semibold text-foreground">
            {t('incidentCategoriesPage.title')}
          </h1>
          <p className="mt-1 text-muted-foreground">{t('incidentCategoriesPage.description')}</p>
        </header>

        <CategoryList
          onCreateClick={() => {
            setCreateOpen(true);
          }}
          onEditClick={(category) => {
            setEditingCategory(category);
          }}
          onToggleActive={(category) => {
            void handleToggleActive(category);
          }}
          onDeleteClick={(category) => {
            setDeletingCategory(category);
          }}
          isAdmin={isAdmin}
        />

        {/* Create Dialog */}
        <CreateCategoryDialog
          open={createOpen}
          onOpenChange={(open) => {
            setCreateOpen(open);
          }}
        />

        {/* Edit Dialog */}
        <EditCategoryDialog
          category={editingCategory}
          open={editingCategory !== null}
          onOpenChange={(open) => {
            if (!open) {
              setEditingCategory(null);
            }
          }}
        />

        {/* Delete Dialog */}
        <DeleteCategoryDialog
          category={deletingCategory}
          open={deletingCategory !== null}
          onOpenChange={(open) => {
            if (!open) {
              setDeletingCategory(null);
            }
          }}
        />
      </div>
    </div>
  );
}
